/**
 * Detail vybrané trasy: časová osa po segmentech, zvýrazněná čekání,
 * instrukce krok za krokem, rozpad nákladů, nejistota a ovládání mapy.
 */
import { dayOffsetSuffix, formatDuration, formatTime } from '../core/time';
import type { Itinerary } from '../core/types';
import { fetchLiveDepartures } from '../data/golemio';
import { el, clear, fmtCzk, fmtKm, fmtCo2, fmtComfort } from './dom';
import { icon, iconOrEmoji } from './icons';

export interface DetailCallbacks {
  onClose(): void;
  onSoloToggle(solo: boolean): void;
  onFocusMap(): void;
}

/** Konfigurace živých odjezdů (Golemio, PID) pro vybranou trasu. */
export interface LiveConfig {
  /** GTFS id nástupních zastávek vybrané trasy. */
  stopIds: string[];
  apiKey: string;
  stopName: string;
}

export function renderDetail(
  container: HTMLElement,
  it: Itinerary | null,
  solo: boolean,
  cb: DetailCallbacks,
  live?: LiveConfig | null,
): void {
  clear(container);
  if (!it) {
    container.style.display = 'none';
    return;
  }
  container.style.display = '';

  const header = el(
    'div',
    { className: 'detail__header' },
    el('span', { 'aria-hidden': 'true' }, iconOrEmoji(it.icon, { size: 20 })),
    el('span', { className: 'detail__title' }, it.name),
    el(
      'button',
      { className: 'btn btn--small', onclick: () => cb.onFocusMap(), title: 'Přiblížit trasu na mapě' },
      icon('map', { size: 13 }),
      ' Na mapě',
    ),
    el(
      'button',
      {
        className: 'btn btn--small',
        'aria-pressed': String(solo),
        onclick: () => cb.onSoloToggle(!solo),
        title: 'Skrýt ostatní trasy na mapě',
      },
      icon(solo ? 'eye' : 'eye-off', { size: 13 }),
      solo ? ' Zobrazit vše' : ' Jen tuto',
    ),
    el(
      'button',
      { className: 'btn btn--small btn--ghost', 'aria-label': 'Zavřít detail', onclick: () => cb.onClose() },
      icon('close', { size: 14 }),
    ),
  );

  const stats = el(
    'div',
    { className: 'detail__stats' },
    stat('Celkem', formatDuration(it.totalMin)),
    stat('Odjezd', formatTime(it.departureMs)),
    stat('Příjezd', formatTime(it.arrivalMs) + dayOffsetSuffix(it.departureMs, it.arrivalMs)),
    stat('Délka', fmtKm(it.distanceKm)),
    stat('Čekání', it.waitMin > 0.4 ? formatDuration(it.waitMin) : 'žádné'),
    stat('Přestupy', String(it.transfers)),
    stat('Cena', fmtCzk(it.cost)),
    stat('Emise', fmtCo2(it.emissions)),
    stat('Spolehlivost', `${Math.round(it.reliability * 100)} % · ±${Math.round(it.uncertaintyMin)} min`),
  );

  // Časová osa.
  const timeline = el('div', { className: 'timeline', role: 'list', 'aria-label': 'Průběh trasy' });
  for (const seg of it.segments) {
    const durMin = seg.endMin - seg.startMin;
    if (durMin < 0.05 && seg.kind !== 'wait') continue;
    const isWait = seg.kind === 'wait' || seg.kind === 'setup' || seg.kind === 'teardown';
    const sub: string[] = [formatDuration(durMin)];
    if (seg.distanceKm > 0.005) sub.push(fmtKm(seg.distanceKm));
    if (seg.kind === 'ride' || seg.kind === 'transit' || seg.kind === 'walk') {
      sub.push(seg.realGeometry ? 'reálná geometrie' : 'odhad trasy');
    }
    if (seg.note) sub.push(seg.note);
    timeline.append(
      el(
        'div',
        { className: `tl-item${isWait ? ' tl-item--wait' : ''}`, role: 'listitem', style: `--seg-color:${isWait ? 'var(--wait)' : seg.color}` },
        el('div', { className: 'tl-item__time' }, formatTime(it.departureMs + seg.startMin * 60_000)),
        el(
          'div',
          { className: 'tl-item__rail', 'aria-hidden': 'true' },
          el('div', { className: 'tl-item__dot' }),
          el('div', { className: 'tl-item__line' }),
        ),
        el(
          'div',
          { className: 'tl-item__body' },
          el('div', { className: 'tl-item__label' }, iconOrEmoji(seg.icon, { size: 14 }), ` ${seg.label}`),
          el('div', { className: 'tl-item__sub' }, sub.join(' · ')),
        ),
      ),
    );
  }
  // Cílový bod.
  timeline.append(
    el(
      'div',
      { className: 'tl-item', role: 'listitem', style: '--seg-color:#c2333f' },
      el('div', { className: 'tl-item__time' }, formatTime(it.arrivalMs) + dayOffsetSuffix(it.departureMs, it.arrivalMs)),
      el('div', { className: 'tl-item__rail', 'aria-hidden': 'true' }, el('div', { className: 'tl-item__dot' })),
      el('div', { className: 'tl-item__body' }, el('div', { className: 'tl-item__label' }, icon('flag', { size: 14 }), ' Cíl')),
    ),
  );

  // Rozpad nákladů.
  const costRows: Array<[string, number]> = [];
  for (const seg of it.segments) {
    if (seg.cost > 0.01) costRows.push([seg.label, seg.cost]);
  }
  const segCostSum = costRows.reduce((s, [, c]) => s + c, 0);
  if (it.cost - segCostSum > 0.5) costRows.push(['Jízdenka / poplatky', it.cost - segCostSum]);

  const costTable =
    it.cost > 0.01
      ? el(
          'table',
          { className: 'cost-breakdown', 'aria-label': 'Rozpad nákladů' },
          ...costRows.map(([label, c]) =>
            el('tr', {}, el('td', {}, label), el('td', {}, fmtCzk(c))),
          ),
          el('tr', {}, el('td', {}, 'Celkem'), el('td', {}, fmtCzk(it.cost))),
        )
      : el('div', { className: 'muted' }, 'Tato varianta je zdarma.');

  const why = el('div', { className: 'detail__why' }, `💡 ${it.why}`);

  const recommend = it.recommendReason
    ? el('div', { className: 'detail__why' }, `🏆 Proč doporučujeme: ${it.recommendReason}`)
    : null;

  const uncertainty = el(
    'div',
    { className: 'muted' },
    `Nejistota odhadu: příjezd ${formatTime(it.arrivalMs)}${dayOffsetSuffix(it.departureMs, it.arrivalMs)} ± ${Math.round(it.uncertaintyMin)} min `,
    `(spolehlivost ${Math.round(it.reliability * 100)} %). `,
    it.dataSource === 'estimate'
      ? 'Výpočet vychází z odhadového modelu (vzdušná vzdálenost × nepřímost), ne z reálných dat.'
      : 'Geometrie trasy pochází z reálného routování; časy jsou modelové.',
  );

  // Živé odjezdy (Golemio) — best effort, načítají se asynchronně.
  let liveBox: HTMLElement | null = null;
  if (live && live.stopIds.length > 0 && it.segments.some((s) => s.kind === 'transit')) {
    liveBox = el('div', { className: 'live-box', role: 'status' }, el('div', { className: 'muted' }, 'Načítám živé odjezdy…'));
    void fetchLiveDepartures(live.stopIds, live.apiKey).then((deps) => {
      if (!liveBox || !liveBox.isConnected) return;
      clear(liveBox);
      if (deps.length === 0) {
        liveBox.append(el('div', { className: 'muted' }, 'Živé odjezdy se nepodařilo načíst (klíč, síť, nebo zastávka mimo PID).'));
        return;
      }
      liveBox.append(el('div', { className: 'card__title', style: 'margin:0 0 6px' }, `Živé odjezdy · ${live.stopName}`));
      for (const d of deps.slice(0, 5)) {
        liveBox.append(
          el(
            'div',
            { className: 'live-row' },
            el('strong', { className: 'live-row__route' }, d.route),
            el('span', { className: 'grow' }, d.headsign),
            el(
              'span',
              { className: 'live-row__time' },
              formatTime(d.predictedMs),
              d.delayMin > 0 ? el('span', { className: 'live-row__delay' }, ` +${Math.round(d.delayMin)} min`) : null,
            ),
          ),
        );
      }
      liveBox.append(el('div', { className: 'muted', style: 'margin-top:4px' }, 'Zdroj: Golemio API (PID), živá data se zpožděními.'));
    });
  }

  container.append(
    header,
    stats,
    el('div', { className: 'card__title', style: 'margin-top:4px' }, 'Časová osa'),
    timeline,
    ...(liveBox ? [liveBox] : []),
    el('div', { className: 'card__title' }, 'Náklady'),
    costTable,
    ...(recommend ? [recommend] : []),
    why,
    uncertainty,
  );
}

function stat(label: string, value: string): HTMLElement {
  return el(
    'div',
    { className: 'stat' },
    el('div', { className: 'stat__label' }, label),
    el('div', { className: 'stat__value' }, value),
  );
}

export { fmtComfort };
